import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type B3DsInputAreaBackground = 'white' | 'grey';

type B3DsInputAreaElement = HTMLElement & {
  value?: string;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  hint?: string;
  error?: boolean;
  background?: B3DsInputAreaBackground;
  id?: string;
  maxlength?: number;
};

@Directive({
  selector: 'b3-input-area[formControlName], b3-input-area[ngModel]',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => B3DsInputAreaDirective),
      multi: true,
    },
  ],
})
export class B3DsInputAreaDirective implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() error = false;
  @Input() background: B3DsInputAreaBackground = 'white';
  @Input() inputId = '';
  @Input() disabled = false;
  @Input() maxlength?: number;

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private value = '';
  private formDisabled = false;

  constructor(
    private readonly elementRef: ElementRef<B3DsInputAreaElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.syncElementState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['label'] ||
      changes['placeholder'] ||
      changes['hint'] ||
      changes['error'] ||
      changes['background'] ||
      changes['inputId'] ||
      changes['disabled'] ||
      changes['maxlength']
    ) {
      this.syncElementState();
    }
  }

  writeValue(value: string | null): void {
    this.value = this.applyMaxLength(value ?? '');
    this.syncElementState();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.syncElementState();
  }

  @HostListener('b3-input', ['$event'])
  onB3Input(event: Event): void {
    const customEvent = event as CustomEvent<{ value?: string }>;
    const rawValue = customEvent.detail?.value ?? '';
    const truncatedValue = this.applyMaxLength(rawValue);

    this.value = truncatedValue;
    this.syncElementState();
    this.onChange(this.value);
  }

  @HostListener('b3-change', ['$event'])
  onB3Change(event: Event): void {
    const customEvent = event as CustomEvent<{ value?: string }>;
    this.value = this.applyMaxLength(customEvent.detail?.value ?? '');
    this.syncElementState();
    this.onChange(this.value);
    this.onTouched();
  }

  @HostListener('b3-blur')
  onB3Blur(): void {
    this.onTouched();
  }

  private syncElementState(): void {
    const element = this.elementRef.nativeElement;
    element.value = this.value;
    element.disabled = this.disabled || this.formDisabled;
    element.error = this.error;
    element.background = this.background;

    this.setOptionalAttribute(element, 'label', this.label);
    this.setOptionalAttribute(element, 'placeholder', this.placeholder);
    this.setOptionalAttribute(element, 'hint', this.hint);
    this.setOptionalAttribute(element, 'background', this.background);
    this.setOptionalAttribute(element, 'value', this.value);

    if (this.inputId) {
      element.id = this.inputId;
      this.renderer.setAttribute(element, 'id', this.inputId);
    }

    this.syncInternalTextareaMaxLength();
  }

  private syncInternalTextareaMaxLength(): void {
    const apply = (): void => {
      const textarea = this.elementRef.nativeElement.shadowRoot?.querySelector('textarea');

      if (!textarea) {
        return;
      }

      if (this.maxlength != null && this.maxlength > 0) {
        textarea.maxLength = this.maxlength;
        this.renderer.setAttribute(textarea, 'maxlength', String(this.maxlength));
        return;
      }

      textarea.removeAttribute('maxlength');
    };

    apply();
    queueMicrotask(apply);
    requestAnimationFrame(apply);
  }

  private applyMaxLength(value: string): string {
    if (this.maxlength == null || this.maxlength <= 0) {
      return value;
    }

    return value.slice(0, this.maxlength);
  }

  private setOptionalAttribute(element: B3DsInputAreaElement, name: string, value: string): void {
    if (value) {
      (element as unknown as Record<string, string>)[name] = value;
      this.renderer.setAttribute(element, name, value);
      return;
    }

    this.renderer.removeAttribute(element, name);
  }
}
